import { useEffect, useState } from "react";
import {
  SLIDE_PANEL_TRANSITION_MS,
  SLIDE_PANEL_UNMOUNT_BUFFER_MS,
} from "./constants";

/**
 * Returns `true` for as long as a SlidePanel consumer should keep its
 * component tree mounted — including during the slide-out CSS transition
 * that plays after `open` switches to `false`.
 *
 * Without this hook, a parent that conditionally renders its panel component
 * (e.g. `{isOpen && <MyPanel open={isOpen} />}`) will unmount the tree the
 * moment `isOpen` becomes false, cutting off the close animation mid-way.
 *
 * This hook owns the transition timing so every consumer gets the correct
 * delay automatically and no one has to hard-code it.
 *
 * @example
 * // In the parent:
 * const isMounted = useSlidePanelMounted(isPanelOpen);
 * return isMounted ? <MyPanel open={isPanelOpen} ... /> : null;
 */
export function useSlidePanelMounted(open: boolean): boolean {
  const [mounted, setMounted] = useState(open);

  useEffect(() => {
    if (open) {
      setMounted(true);
    } else {
      // Keep mounted for the full slide-out transition + a small buffer so
      // the panel fully disappears before React removes it from the tree.
      const t = setTimeout(
        () => setMounted(false),
        SLIDE_PANEL_TRANSITION_MS + SLIDE_PANEL_UNMOUNT_BUFFER_MS,
      );
      return () => clearTimeout(t);
    }
  }, [open]);

  return mounted;
}
